import { useEffect, useState } from "react";
import type { Me } from "../../api/schemas";
import { useOffline } from "../../offline/OfflineContext";
import type { RoleSnapshot } from "../../offline/model";
import { storedWorkTimeZone } from "./roleSnapshot";
import { useOwnerKey } from "./tenantSelection";
import { useMe } from "./useMe";

/** Sources of the time zone in which the clock screen and the icon badge take "today". */
export interface WorkTimeZoneSources {
  /** Profile from `GET /me` of this session, or `undefined` while it loads, is paused or failed. */
  readonly profile: Me | undefined;
  /** Whether `GET /me` is still loading. */
  readonly profileLoading: boolean;
  /**
   * Employment time zone stored on this device with the last profile ({@link storedWorkTimeZone}):
   * `undefined` while the local store has not answered for the signed-in person, `null` if nothing
   * usable is stored, else the IANA zone.
   */
  readonly stored: string | null | undefined;
  /** IANA time zone of the device. */
  readonly deviceTimeZone: string;
}

/**
 * Chooses the time zone of "today" (F-18, ADR-0132): the employment's (establishment) from the
 * profile; while the server has not answered, the one of the last profile stored on this device, so
 * an app start without connection uses the same zone as the synchronization; the device's without
 * employment or once the profile failed without a stored zone. The choice waits (`null`) while the
 * local store has not answered and no profile is known (offline `GET /me` is paused, not loading),
 * and while the profile loads without a stored zone, instead of starting in the device's zone.
 *
 * @param sources - The sources.
 * @returns The IANA time zone, or `null` while it is not known yet.
 */
export function selectWorkTimeZone(sources: WorkTimeZoneSources): string | null {
  if (sources.profile !== undefined) {
    const fromProfile = sources.profile.employment?.timeZoneId ?? sources.deviceTimeZone;
    return fromProfile;
  }

  if (sources.stored === undefined) {
    return null;
  }

  if (sources.stored !== null) {
    return sources.stored;
  }

  const fallback = sources.profileLoading ? null : sources.deviceTimeZone;

  return fallback;
}

/** Stored zone read for an owner. */
interface StoredZone {
  /** Owner key the snapshot was read for. */
  readonly owner: string;
  /** Usable employment time zone of the snapshot, `null` if none. */
  readonly timeZoneId: string | null;
}

/**
 * Returns the time zone in which the clock screen and the icon badge take "today": that of the
 * employment, also before `GET /me` answers when it is stored on this device ({@link selectWorkTimeZone}).
 *
 * @returns The IANA time zone, or `null` while it is not known yet.
 */
export function useWorkTimeZone(): string | null {
  const me = useMe();
  const owner = useOwnerKey();
  const { store, nowMs, deviceTimeZone } = useOffline();
  const [stored, setStored] = useState<StoredZone | null>(null);

  useEffect(() => {
    if (owner === null) {
      return undefined;
    }

    let active = true;
    const apply = (snapshot: RoleSnapshot | null): void => {
      if (active) {
        setStored({ owner, timeZoneId: storedWorkTimeZone(snapshot, owner, nowMs()) });
      }
    };
    store.getRoleSnapshot(owner).then(apply, () => {
      apply(null);
    });
    const cleanup = (): void => {
      active = false;
    };

    return cleanup;
  }, [store, nowMs, owner]);

  // Without a signed-in person nothing is stored; otherwise only the read for this owner counts,
  // `undefined` until it has answered.
  const forOwner = stored?.owner === owner ? stored.timeZoneId : undefined;
  const storedZone = owner === null ? null : forOwner;
  const timeZone = selectWorkTimeZone({
    profile: me.data,
    profileLoading: me.isLoading,
    stored: storedZone,
    deviceTimeZone,
  });

  return timeZone;
}
